type NotificationType = "error" | "warning" | "info" | "success";

interface ValidationErrorData {
  message?: string;
  error?: string;
  errors?: {
    title?: string[];
    message?: string[];
    type?: NotificationType[];
  };
  metadata?: {
    treatment_id?: number;
    patient_id?: number;
  };
}

type ToastConfig = Parameters<ReturnType<typeof useToast>["add"]>[0];

const NOTIFICATION_CONFIG = {
  error: { color: "error", icon: "i-heroicons-exclamation-triangle" },
  warning: { color: "warning", icon: "i-heroicons-exclamation-circle" },
  info: { color: "secondary", icon: "i-heroicons-information-circle" },
  success: { color: "primary", icon: "i-heroicons-check-circle" },
} as const;

export default defineNuxtPlugin({
  name: "api-error-handler",
  enforce: "post",
  setup(nuxtApp) {
    const i18n = nuxtApp.$i18n as { t: (key: string) => string };

    const getErrorMessage = (error: {
    response?: { status?: number; data?: ValidationErrorData & { errors?: Record<string, string[]> } };
  }): string | undefined => {
    if (error?.response?.status !== 422) {
      return undefined;
    }

    const data = error.response?.data;
    if (!data) {
      return undefined;
    }

    const errorKey = data.error;
    const messageKey = data.errors?.message?.[0];

    if (errorKey) {
      return errorKey.startsWith("errors.") || errorKey.includes(".") ? i18n.t(errorKey) : errorKey;
    }

    if (messageKey) {
      return messageKey.startsWith("errors.") || messageKey.includes(".") ? i18n.t(messageKey) : messageKey;
    }

    if (data.errors && typeof data.errors === "object") {
      const fieldErrors = data.errors as Record<string, string[]>;
      const firstFieldError = Object.values(fieldErrors).find(
        (messages) => Array.isArray(messages) && messages.length > 0,
      );

      if (firstFieldError && firstFieldError[0]) {
        const errorMessage = firstFieldError[0];
        if (
          errorMessage.startsWith("errors.") ||
          errorMessage.startsWith("events.") ||
          errorMessage.startsWith("treatment.") ||
          errorMessage.startsWith("declaration.") ||
          errorMessage.startsWith("validation.")
        ) {
          try {
            return i18n.t(errorMessage);
          } catch {
            return errorMessage;
          }
        }
        return errorMessage;
      }
    }

    return undefined;
  };

  const handleValidationError = (error: {
    response?: { status: number; data?: ValidationErrorData & { errors?: Record<string, string[]> } };
  }) => {
    if (error?.response?.status !== 422 || !nuxtApp.$pinia) return;
    nuxtApp.runWithContext(() => {
      try {
        const toast = useToast();

        const data = error.response?.data;
        const errorKey = data?.error;
        const titleKey = data?.errors?.title?.[0];
        const messageKey = data?.errors?.message?.[0];
        const notificationType: NotificationType = data?.errors?.type?.[0] ?? "error";

        let errorTitle = i18n.t("errors.validationErrorTitle");
        let errorMessage: string | undefined;

        if (errorKey) {
          errorMessage = errorKey.startsWith("errors.") || errorKey.includes(".") ? i18n.t(errorKey) : errorKey;
        } else if (messageKey) {
          errorMessage = messageKey.startsWith("errors.") || messageKey.includes(".") ? i18n.t(messageKey) : messageKey;
        } else if (titleKey) {
          errorTitle = titleKey.startsWith("errors.") || titleKey.includes(".") ? i18n.t(titleKey) : titleKey;
        } else if (data?.errors && typeof data.errors === "object") {
          const fieldErrors = data.errors as Record<string, string[]>;
          const firstFieldError = Object.values(fieldErrors).find(
            (messages) => Array.isArray(messages) && messages.length > 0,
          );

          if (firstFieldError && firstFieldError[0]) {
            const errorMsg = firstFieldError[0];
            if (
              errorMsg.startsWith("errors.") ||
              errorMsg.startsWith("events.") ||
              errorMsg.startsWith("treatment.") ||
              errorMsg.startsWith("declaration.") ||
              errorMsg.startsWith("validation.")
            ) {
              try {
                errorMessage = i18n.t(errorMsg);
              } catch {
                errorMessage = errorMsg;
              }
            } else {
              errorMessage = errorMsg;
            }
          }
        }

        if (!errorMessage) {
          errorMessage = i18n.t("errors.validationErrorAccured");
        }

        const { color, icon } = NOTIFICATION_CONFIG[notificationType];

        const toastConfig: ToastConfig = {
          title: errorTitle,
          description: errorMessage,
          color,
          icon,
          duration: 8000,
        };

        // TODO: We need a better scalable way to define which actions to add and when: https://vernehealth.atlassian.net/browse/VEP-1579
        // Maybe returning a translation key and let the front-end handle the rest
        const metadata = data?.metadata;
        if (metadata?.treatment_id && metadata?.patient_id) {
          toastConfig.actions = [
            {
              label: i18n.t("actions.edit_treatment"),
              onClick: () => {
                navigateTo(`/patients/${metadata.patient_id}/treatment/${metadata.treatment_id}`);
              },
            },
          ];
        } else if (metadata?.patient_id) {
          toastConfig.actions = [
            {
              label: i18n.t("actions.edit_patient"),
              onClick: () => {
                navigateTo(`/patients/${metadata.patient_id}`);
              },
            },
          ];
        }

        toast.add(toastConfig);
      } catch {
        // Pinia not ready
      }
    });
  };

    return {
      provide: {
        handleValidationError,
        getErrorMessage,
      },
    };
  },
});
